import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { QuickReply } from '../types';

/**
 * Schnellantworten-Picker (Textbausteine). Wird per Button oder durch Tippen
 * von „/" in der Compose-Box geöffnet. Auswahl fügt `body` ein.
 *
 * Such-Combobox (erfüllt die UI-Regel für > 4 Optionen): filtert client-seitig
 * über Titel, Shortcut und Body.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (body: string) => void;
}

export function QuickReplyPicker({ open, onClose, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data } = useQuery({
    queryKey: ['quick-replies'],
    queryFn: () => api.get<{ items: QuickReply[] }>('/api/quick-replies'),
    enabled: open,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.body.toLowerCase().includes(q) ||
        (r.shortcut?.toLowerCase().includes(q) ?? false),
    );
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => setActiveIndex(0), [filtered.length]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onClose]);

  if (!open) return null;

  function pick(r: QuickReply) {
    onSelect(r.body);
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = filtered[activeIndex];
      if (r) pick(r);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  return (
    <div
      ref={rootRef}
      className="absolute bottom-full left-0 z-30 mb-1 w-80 rounded-md border border-kalkuel-border bg-white shadow-brand-lg"
    >
      <div className="relative border-b border-kalkuel-border p-2">
        <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-kalkuel-muted" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Schnellantwort suchen…"
          className="h-8 w-full rounded-pill border border-kalkuel-border bg-kalkuel-surface pl-8 pr-3 text-sm placeholder:text-kalkuel-muted focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
        />
      </div>
      <ul className="max-h-64 overflow-y-auto py-1">
        {filtered.length === 0 && (
          <li className="px-3 py-2 text-xs text-kalkuel-muted">Keine Schnellantworten.</li>
        )}
        {filtered.map((r, idx) => (
          <li key={r.id}>
            <button
              type="button"
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => pick(r)}
              className={[
                'block w-full px-3 py-2 text-left',
                idx === activeIndex ? 'bg-kalkuel-accent-soft' : 'hover:bg-kalkuel-surface',
              ].join(' ')}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-kalkuel-primary">{r.title}</span>
                {r.shortcut && (
                  <span className="rounded-pill bg-kalkuel-surface px-1.5 py-0.5 text-[10px] text-kalkuel-muted">
                    /{r.shortcut}
                  </span>
                )}
              </div>
              <div className="line-clamp-2 text-[11px] text-kalkuel-muted">{r.body}</div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
